import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const StrokeFH = () => {
  const mechanisms = [
    {
      number: '1',
      title: 'Carotid Artery Atherosclerosis',
      content: 'The carotid arteries, located in the neck, supply blood to the brain. In individuals with FH: LDL particles accumulate in carotid artery walls, plaque forms earlier than in the general population, and carotid intima-media thickness increases. Carotid plaque is a strong predictor of ischemic stroke. Ultrasound studies show that even young adults with untreated FH may demonstrate early carotid artery thickening.',
      icon: 'ri-brain-line',
      color: '#f04e58'
    },
    {
      number: '2',
      title: 'Plaque Rupture and Clot Formation',
      content: 'Atherosclerotic plaques are not always stable. In FH: High LDL promotes inflammatory activity inside plaques, plaque caps may weaken, and rupture can trigger clot formation. If a clot forms in the carotid artery or travels to cerebral arteries, it can block blood flow to the brain — causing an ischemic stroke.',
      icon: 'ri-alert-line',
      color: '#e67e22'
    },
    {
      number: '3',
      title: 'Lipoprotein(a) and Stroke Risk',
      content: 'Many individuals with FH also have elevated Lipoprotein(a), or Lp(a). Lp(a): promotes clot formation, interferes with clot breakdown, and increases vascular inflammation. Elevated Lp(a) is independently associated with higher stroke risk, especially in younger patients. When FH and high Lp(a) coexist, cerebrovascular risk increases further.',
      icon: 'ri-flask-line',
      color: '#ec4899'
    },
    {
      number: '4',
      title: 'Hypertension as a Compounding Factor',
      content: 'Hypertension is one of the strongest risk factors for stroke. When combined with FH: Endothelial injury worsens, arterial stiffness increases, and plaque rupture risk rises. This dual burden significantly elevates stroke probability.',
      icon: 'ri-heart-pulse-line',
      color: '#8b5cf6'
    },
    {
      number: '5',
      title: 'Diabetes and Insulin Resistance',
      content: 'If FH coexists with diabetes or insulin resistance: Vascular inflammation increases, endothelial dysfunction worsens, and plaque progression accelerates. This metabolic combination can heighten both coronary and cerebrovascular risk.',
      icon: 'ri-syringe-line',
      color: '#f59e0b'
    },
    {
      number: '6',
      title: 'Premature Stroke',
      content: 'Although stroke is more common later in life, FH can increase the risk of premature ischemic stroke, particularly in: Individuals with very high LDL, those with elevated Lp(a), and patients with poorly controlled additional risk factors. While stroke risk in FH is generally lower than heart attack risk, it remains clinically significant.',
      icon: 'ri-time-line',
      color: '#ec4899'
    },
    {
      number: '7',
      title: 'Silent Cerebrovascular Disease',
      content: 'Not all strokes cause dramatic symptoms. In some cases: Small vessel blockages may occur silently, mild cognitive changes may develop, and brain imaging may reveal subclinical infarcts. Early vascular screening can identify risk before major events occur.',
      icon: 'ri-eye-off-line',
      color: '#0d9488'
    },
    {
      number: '8',
      title: 'Gender Considerations',
      content: 'Women with FH may be underdiagnosed and undertreated. After menopause: LDL levels often rise further, estrogen protection declines, and stroke risk increases. Awareness during midlife is especially important for women with elevated cholesterol and family history.',
      icon: 'ri-user-heart-line',
      color: '#d946ef'
    },
    {
      number: '9',
      title: 'Screening and Prevention',
      content: 'Individuals with FH should consider: Lipid panel monitoring, Lipoprotein(a) measurement, blood pressure management, carotid ultrasound (in selected cases), and coronary calcium scoring. Early and aggressive LDL reduction significantly lowers risk of both heart attack and stroke.',
      icon: 'ri-stethoscope-line',
      color: '#06b6d4'
    },
    {
      number: '10',
      title: 'Treatment Strategies',
      content: 'Effective management includes aggressive LDL lowering (high-intensity statins, ezetimibe, PCSK9 inhibitors, inclisiran, LDL apheresis in severe cases) and risk factor control (strict blood pressure control, diabetes management, smoking cessation, weight optimization, physical activity). Reducing cumulative LDL exposure — known as "cholesterol-year burden" — is key.',
      icon: 'ri-medicine-bottle-line',
      color: '#6366f1'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-brain-line text-[#f04e58]"></i>
              Understanding Stroke Risk in FH Patients
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Stroke + <br />
              Familial Hypercholesterolemia (FH)
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Understanding the hidden risk of ischemic stroke in patients with inherited high cholesterol
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Stroke and Familial Hypercholesterolemia (FH): Understanding the Hidden Risk
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                When people hear about Familial Hypercholesterolemia (FH), they often think about heart attacks.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                But stroke — particularly ischemic stroke — is another serious and potentially life-altering complication linked to lifelong elevated LDL cholesterol.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                While coronary artery disease is the most common outcome of FH, the same atherosclerotic process that narrows heart arteries can also affect arteries supplying the brain. Raising awareness about stroke risk in FH is essential for early prevention and family screening.
              </p>
            </div>
          </div>

          {/* What is a Stroke */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              What Is a Stroke?
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              A stroke occurs when blood flow to part of the brain is interrupted.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-br from-[#f04e58]/10 to-[#f04e58]/5 rounded-2xl p-6 border-l-4 border-[#f04e58]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#f04e58]/20">
                    <i className="ri-close-circle-line text-xl text-[#f04e58]"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Ischemic Stroke</h4>
                </div>
                <p className="text-sm text-gray-600">
                  <strong>Most common:</strong> Caused by blockage in a brain artery
                </p>
              </div>
              <div className="bg-gradient-to-br from-[#ec4899]/10 to-[#ec4899]/5 rounded-2xl p-6 border-l-4 border-[#ec4899]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#ec4899]/20">
                    <i className="ri-drop-line text-xl text-[#ec4899]"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900">Hemorrhagic Stroke</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Caused by bleeding in the brain
                </p>
              </div>
            </div>
            <div className="bg-[#fef9f9] rounded-xl p-6 border-l-4 border-[#f04e58]">
              <p className="text-gray-700 leading-relaxed">
                <strong>In FH, the primary concern is ischemic stroke driven by atherosclerosis.</strong>
              </p>
            </div>
          </div>

          {/* How FH Increases Stroke Risk */}
          <div className="bg-gradient-to-br from-[#fef9f9] to-white rounded-2xl p-8 mb-12 border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              How FH Increases Stroke Risk
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              FH causes LDL cholesterol levels to be elevated from birth.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Over time, this leads to:
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Accelerated plaque formation</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Vascular inflammation</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Arterial narrowing</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Plaque instability</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Although the coronary arteries are most commonly affected, arteries in the neck and brain can also develop atherosclerosis.
            </p>
          </div>

          {/* Image */}
          <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/3f17f8da456078fdbb702ea6a67b9076.jpg"
              alt="Carotid artery atherosclerosis affecting brain blood flow"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Mechanisms Section */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-white to-[#faf5f8]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How Stroke Risk Develops in FH
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Ten critical mechanisms linking Familial Hypercholesterolemia to cerebrovascular disease
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {mechanisms.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full text-white font-bold text-sm"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.number}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Family Screening Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#0d9488] to-[#14b8a6] rounded-2xl p-10 text-white mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                <i className="ri-group-line text-3xl text-white"></i>
              </div>
              <div>
                <h2 className="text-3xl font-semibold mb-2">The Importance of Family Screening</h2>
                <p className="text-lg text-white/90">FH is inherited in an autosomal dominant pattern.</p>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm mb-6">
              <p className="text-base text-white/90 mb-4">
                If one parent has FH:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-sm text-white"></i>
                  </div>
                  <span className="text-white/90">Each child has a <strong className="text-white">50% chance</strong> of inheriting it</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-sm text-white"></i>
                  </div>
                  <span className="text-white/90">Early identification allows preventive treatment long before stroke or heart attack occurs</span>
                </li>
              </ul>
            </div>

            <p className="text-base text-white font-semibold">
              Cascade family screening can save multiple lives across generations.
            </p>
          </div>
        </div>
      </section>

      {/* Raising Awareness Section */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Raising Awareness</h2>
            <p className="text-lg text-gray-600">A preventable crisis hiding in plain sight</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white rounded-2xl border-2 border-[#f04e58]/20 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#f04e58]/10 mx-auto mb-4">
                <i className="ri-group-line text-3xl text-[#f04e58]"></i>
              </div>
              <div className="text-4xl font-bold text-[#f04e58] mb-2">1 in 250</div>
              <p className="text-sm text-gray-600">People worldwide affected by FH</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-[#dc2626]/20 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#dc2626]/10 mx-auto mb-4">
                <i className="ri-eye-off-line text-3xl text-[#dc2626]"></i>
              </div>
              <div className="text-4xl font-bold text-[#dc2626] mb-2">Most</div>
              <p className="text-sm text-gray-600">Remain undiagnosed</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-[#e67e22]/20 p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#e67e22]/10 mx-auto mb-4">
                <i className="ri-alert-line text-3xl text-[#e67e22]"></i>
              </div>
              <div className="text-4xl font-bold text-[#e67e22] mb-2">90%</div>
              <p className="text-sm text-gray-600">Undiagnosed worldwide</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                <i className="ri-error-warning-line text-2xl text-[#f04e58]"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">If you or a family member has:</h3>
                <ul className="space-y-3 text-base text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-1 flex-shrink-0"></i>
                    <span>LDL above 190 mg/dL</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-1 flex-shrink-0"></i>
                    <span>Family history of early heart disease</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-1 flex-shrink-0"></i>
                    <span>Premature stroke</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f04e58] mt-1 flex-shrink-0"></i>
                    <span>Recurrent vascular events</span>
                  </li>
                </ul>
              </div>
            </div>
            <p className="text-base text-gray-900 font-semibold mt-6">
              Consider evaluation for Familial Hypercholesterolemia.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/10 mx-auto mb-6">
                <i className="ri-lightbulb-line text-4xl text-white"></i>
              </div>
              <h2 className="text-3xl font-semibold mb-4">Final Takeaway</h2>
            </div>

            <div className="space-y-4 mb-8">
              <p className="text-lg text-white/90 leading-relaxed">
                Stroke is a devastating and often life-changing event.
              </p>
              
              <p className="text-lg text-white/90 leading-relaxed">
                While heart attacks are more commonly associated with FH, the same LDL-driven vascular injury can affect arteries supplying the brain.
              </p>

              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                <p className="text-base text-white/90 mb-3">The encouraging news is that:</p>
                <ul className="space-y-2 text-base text-white/90">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-1 flex-shrink-0"></i>
                    <span>Early detection and aggressive LDL control can dramatically reduce long-term risk</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#14b8a6] mt-1 flex-shrink-0"></i>
                    <span>Awareness of Familial Hypercholesterolemia does not just protect the heart — it protects the brain</span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-white font-semibold text-center mt-6">
                And in the case of FH, prevention begins with knowing.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14b8a6]/20 mx-auto mb-3">
                  <i className="ri-eye-line text-2xl text-[#14b8a6]"></i>
                </div>
                <h3 className="text-base font-semibold mb-1">Awareness</h3>
                <p className="text-sm text-white/80">Know the signs</p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14b8a6]/20 mx-auto mb-3">
                  <i className="ri-test-tube-line text-2xl text-[#14b8a6]"></i>
                </div>
                <h3 className="text-base font-semibold mb-1">Testing</h3>
                <p className="text-sm text-white/80">Get screened</p>
              </div>
              
              <div className="bg-white/10 rounded-xl p-5 backdrop-blur-sm text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14b8a6]/20 mx-auto mb-3">
                  <i className="ri-heart-pulse-line text-2xl text-[#14b8a6]"></i>
                </div>
                <h3 className="text-base font-semibold mb-1">Early Treatment</h3>
                <p className="text-sm text-white/80">Prevent complications</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Early Detection Saves Lives
          </h2>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            If you have a family history of early heart disease, stroke, or high cholesterol, don't wait for symptoms. Early screening and aggressive LDL control can prevent these devastating outcomes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/consultation"
              className="inline-flex items-center px-8 py-4 bg-[#f04e58] text-white rounded-full font-semibold hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-[#f04e58]/20"
            >
              Book a Free Consultation
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/topics"
              className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:border-[#f04e58] hover:text-[#f04e58] transition-all cursor-pointer whitespace-nowrap"
            >
              Learn More About FH
              <i className="ri-book-open-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StrokeFH;